import { m } from "@plannello/i18n/messages";
import { getLocale } from "@plannello/i18n/runtime";
import { type Localized, localize } from "@plannello/shared";

/** User-defined names (types, statuses, labels, fields) in the current UI language. */
export function loc(value: Localized | string | null | undefined): string {
  return localize(value, getLocale());
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * Compact age for dense rows: "now", "5m", "3h", "4d", "2w" (German: "5 Min", "3 Std", "4 Tg",
 * "2 Wo"), then a short date ("12 Sep", "12 Sep 2025" in another year). Units are messages:
 * Intl narrow units are inconsistent in German ("2 M" for both minutes and months). Callers show
 * the full date in a title.
 */
export function shortAge(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  const d = Math.max(0, now - t);
  if (d < MIN) return m.time_now();
  if (d < HOUR) return m.age_minutes({ n: Math.floor(d / MIN) });
  if (d < DAY) return m.age_hours({ n: Math.floor(d / HOUR) });
  if (d < 14 * DAY) return m.age_days({ n: Math.floor(d / DAY) });
  if (d < 8 * 7 * DAY) return m.age_weeks({ n: Math.floor(d / (7 * DAY)) });
  return shortDate(iso, now);
}

export function shortDate(iso: string, now = Date.now()): string {
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat(getLocale(), {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(date);
}

/** Full date and time for tooltips and titles. */
export function fullDateTime(iso: string): string {
  return new Intl.DateTimeFormat(getLocale(), { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * DAY],
  ["month", 30 * DAY],
  ["week", 7 * DAY],
  ["day", DAY],
  ["hour", HOUR],
  ["minute", MIN],
];

/** "3 hours ago" / "vor 3 Stunden" for activity and comments. */
export function relativeTime(iso: string, now = Date.now()): string {
  const diff = Date.parse(iso) - now;
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  // Intl says "this minute" / "in dieser Minute"; people say "just now".
  return m.time_just_now();
}

/** Label colours are stored as names; each maps onto one of the eight --label-N swatch tokens. */
const TINT: Record<string, number> = {
  red: 1,
  orange: 2,
  amber: 2,
  yellow: 3,
  lime: 4,
  green: 4,
  teal: 5,
  cyan: 5,
  blue: 6,
  sky: 6,
  indigo: 7,
  violet: 7,
  purple: 7,
  pink: 8,
  rose: 8,
};
export function labelSwatch(color: string | undefined): string {
  if (color?.toLowerCase() === "slate") return "var(--label-slate)";
  const n = color ? TINT[color.toLowerCase()] : undefined;
  return n ? `var(--label-${n})` : "var(--label-gray)";
}

/** Palette name (violet is an alias of purple) to the suffix of the `--type-*` and `--status-accent-*` tokens. */
const HUE: Record<string, string> = {
  gray: "gray",
  red: "red",
  orange: "orange",
  yellow: "yellow",
  green: "green",
  teal: "teal",
  blue: "blue",
  purple: "purple",
  violet: "purple",
  pink: "pink",
  slate: "slate",
};

/** Tint of an item type's glyph (>= 3:1 on every surface). Undefined when the colour is missing or unknown. */
export function typeTint(color: string | null | undefined): string | undefined {
  const h = color ? HUE[color.toLowerCase()] : undefined;
  return h && h !== "slate" ? `var(--type-${h})` : undefined;
}

/** 2px accent (bar or border) in a status's custom colour. Undefined when the colour is missing or unknown. */
export function statusAccent(color: string | null | undefined): string | undefined {
  const h = color ? HUE[color.toLowerCase()] : undefined;
  return h ? `var(--status-accent-${h})` : undefined;
}
